import { IconX } from "@btnet/blueprint-icons";
import { useIsNativeBackReplacingClose } from "@btnet/utilities-webview/NativeBackCloseContext";

import { BTButton } from "../BTButton/BTButton";
import {
  type IHotkeyWithPopoverConfig,
  type THotkeyCommands,
} from "../Hotkey/hotkey.types";

export interface IBTNativeBackCloseButtonProps {
  /** @example "field-picker-close" */
  "data-testid": string;
  /** Closes the surface. Wired to the same handler its host's `onClose` uses. */
  onClick: (event?: React.MouseEvent<HTMLElement>) => void;
  /**
   * Accessible name for the button. Defaults to "Close"; override when a screen reader
   * needs to distinguish this close from others in the same view.
   * @default "Close"
   */
  "aria-label"?: string;
  className?: string;
  id?: string;
  hotkey?: THotkeyCommands | IHotkeyWithPopoverConfig;
}

/**
 * The canonical close (X) for a drawer, bottom sheet, split-view panel, or any other
 * dismissible surface — anywhere the mobile native back button can take over closing.
 *
 * BTDrawer hides its own built-in close centrally when native back takes over, but a
 * hand-placed close control isn't covered by that switch. This component reads
 * {@link useIsNativeBackReplacingClose} itself: renders nothing when back has claimed
 * closing, otherwise a standard tertiary X.
 */
export function BTNativeBackCloseButton({
  onClick,
  "aria-label": ariaLabel = "Close",
  ...otherProps
}: IBTNativeBackCloseButtonProps) {
  const isNativeBackReplacingClose = useIsNativeBackReplacingClose();
  if (isNativeBackReplacingClose) {
    return null;
  }

  return (
    <BTButton
      type="tertiary"
      icon={<IconX />}
      aria-label={ariaLabel}
      onClick={onClick}
      {...otherProps}
    />
  );
}
